import React, {useMemo} from 'react';
import {NumberInput, Select, TextInput} from '@mantine/core';
import {Table} from 'src/design/atoms/Table';
import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {ScrollArea} from 'src/design/atoms/ScrollArea';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconExternalLink, IconSearch} from '@tabler/icons-react';
import fileDownload from 'js-file-download';
import {Link} from 'react-router-dom';
import {SkillMatrixStatusEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {Paginator} from 'src/design/organisms/Pagination';
import {useListState} from 'src/design/organisms/useListState';
import {Pill} from 'src/design/atoms/Pill';
import {Spinner} from 'src/design/atoms/Spinner';
import {useGetDepartmentSelectOptionsQuery} from 'src/redux/queries/department';
import {
  useGetSkillMatrixQuery,
  useLazyGetSkillMatrixQuery,
} from 'src/redux/queries/skillSignOff';
import {useGetVendorSelectOptionsQuery} from 'src/redux/queries/vendor';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

const PAGE_SIZE = 25;

// Large enough to cover any realistic instance roster in one unpaginated
// export fetch — CSV export must cover the whole filtered roster, not just
// the current 25-row page (see `handleExport`).
const EXPORT_FETCH_LENGTH = 100000;

interface SkillMatrixFilters {
  departmentId: string | null;
  vendorId: string | null;
  statusFilter: string | null;
  expiringWithinDays: number | '';
}

const DEFAULT_MATRIX_FILTERS: SkillMatrixFilters = {
  departmentId: null,
  vendorId: null,
  statusFilter: null,
  expiringWithinDays: 30,
};

const STATUS_FILTER_OPTIONS = [
  {value: String(SkillMatrixStatusEnum.Never), label: 'Never'},
  {value: String(SkillMatrixStatusEnum.SignedOff), label: 'Signed off'},
  {value: String(SkillMatrixStatusEnum.Expiring), label: 'Expiring'},
  {value: String(SkillMatrixStatusEnum.Expired), label: 'Expired'},
];

const STATUS_PILL: Record<
  SkillMatrixStatusEnum,
  {text: string; color?: string}
> = {
  0: {text: 'Never', color: 'gray'},
  1: {text: 'Signed off', color: 'green'},
  2: {text: 'Expiring', color: 'yellow'},
  3: {text: 'Expired', color: 'red'},
};

export const skillMatrixStatusPill = (status: SkillMatrixStatusEnum) =>
  STATUS_PILL[status] ?? {text: 'Unknown', color: 'gray'};

const toCsv = (
  skills: {skillId: number; name: string}[],
  rows: {
    userName: string;
    employeeId?: string | null;
    cells: {skillId: number; status: SkillMatrixStatusEnum}[];
  }[],
  statusFilter?: SkillMatrixStatusEnum
): string => {
  const filteredRows =
    statusFilter === undefined
      ? rows
      : rows.filter(row => row.cells.some(cell => cell.status === statusFilter));

  const header = ['Person', 'Employee ID', ...skills.map(skill => skill.name)];
  const lines = filteredRows.map(row => {
    const cellsBySkill = new Map(row.cells.map(cell => [cell.skillId, cell]));
    return [
      row.userName,
      row.employeeId ?? '',
      ...skills.map(
        skill =>
          skillMatrixStatusPill(
            cellsBySkill.get(skill.skillId)?.status ?? SkillMatrixStatusEnum.Never
          ).text
      ),
    ].join(',');
  });
  return [header.join(','), ...lines].join('\n');
};

/**
 * Skill matrix — people × skills, the auditor artifact. Skills scroll
 * horizontally in their own container since the column count is
 * unbounded and driven by data, not layout. Server-side search/department/
 * vendor/pagination via `SkillMatrixRequest`; status is filtered client-side
 * (no server field) against the already-fetched page of rows. Cells carry
 * `signOffId` (latest SignedOff row; undefined for Never/no record) — the
 * small link icon opens that record's detail view; no row-click navigation
 * per convention.
 */
export const SkillMatrixTab: React.FC = () => {
  const {setAlert} = useAlert();
  const {
    searchTerm: search,
    setSearchTerm: setSearch,
    filters,
    setFilters,
    currentPage: page,
    handlePageChange,
    start,
    length,
  } = useListState<SkillMatrixFilters>({
    identifier: 'training-skill-matrix',
    pageSize: PAGE_SIZE,
    defaultFilters: DEFAULT_MATRIX_FILTERS,
  });
  const {departmentId, vendorId, statusFilter, expiringWithinDays} = filters;
  const setFilter = <K extends keyof SkillMatrixFilters>(
    key: K,
    value: SkillMatrixFilters[K]
  ) => setFilters(prev => ({...prev, [key]: value}));

  const {data: departments} = useGetDepartmentSelectOptionsQuery({
    start: 0,
    length: 1000,
  });
  const {data: vendors} = useGetVendorSelectOptionsQuery({
    start: 0,
    length: 1000,
    active: true,
  });

  const matrixFilters = {
    search: search.trim() || undefined,
    departmentId: departmentId ? Number(departmentId) : undefined,
    vendorId: vendorId ? Number(vendorId) : undefined,
    expiringWithinDays: expiringWithinDays === '' ? 30 : expiringWithinDays,
  };

  const {data, isLoading, isFetching, isError, error} = useGetSkillMatrixQuery({
    start,
    length,
    ...matrixFilters,
  });
  const [fetchFullMatrix, {isFetching: isExporting}] =
    useLazyGetSkillMatrixQuery();

  const skills = data?.skills ?? [];
  const statusFilterValue = statusFilter
    ? (Number(statusFilter) as SkillMatrixStatusEnum)
    : undefined;
  const rows = useMemo(() => {
    const allRows = data?.rows ?? [];
    if (statusFilterValue === undefined) {
      return allRows;
    }
    return allRows.filter(row =>
      row.cells.some(cell => cell.status === statusFilterValue)
    );
  }, [data?.rows, statusFilterValue]);

  // Export must cover the whole filtered roster, not just the current
  // 25-row page — fetch unpaginated (respecting search/department/vendor)
  // via a separate lazy query rather than exporting the already-paged
  // `rows`; the status filter has no server field, so it's applied inside
  // `toCsv` against the full result.
  const handleExport = async () => {
    try {
      const fullMatrix = await fetchFullMatrix({
        start: 0,
        length: EXPORT_FETCH_LENGTH,
        ...matrixFilters,
      }).unwrap();
      const csv = toCsv(
        fullMatrix.skills,
        fullMatrix.rows,
        statusFilterValue
      );
      fileDownload(
        csv,
        `skill_matrix_${new Date().toISOString().slice(0, 10)}.csv`
      );
    } catch (exportError) {
      setAlert(parseErrorMessage(exportError), 'error');
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="md" wrap="wrap" align="flex-end">
        <Group wrap="wrap" align="flex-end">
          <TextInput
            label="Search"
            placeholder="Person, employee ID or skill"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={event => {
              setSearch(event.currentTarget.value);
            }}
            w={220}
          />
          <Select
            label="Department"
            placeholder="Any department"
            clearable
            data={(departments?.data ?? []).map(option => ({
              value: String(option.id),
              label: option.label ?? '',
            }))}
            value={departmentId}
            onChange={value => {
              setFilter('departmentId', value);
            }}
            w={180}
          />
          <Select
            label="Vendor"
            placeholder="Any vendor"
            clearable
            data={(vendors?.data ?? []).map(option => ({
              value: String(option.id),
              label: option.label ?? '',
            }))}
            value={vendorId}
            onChange={value => {
              setFilter('vendorId', value);
            }}
            w={180}
          />
          <Select
            label="Status"
            placeholder="Any status"
            clearable
            data={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={value => {
              setFilter('statusFilter', value);
            }}
            w={160}
          />
          <NumberInput
            label="Expiring within (days)"
            min={1}
            value={expiringWithinDays}
            onChange={value => {
              setFilter(
                'expiringWithinDays',
                value === '' ? '' : Number(value)
              );
            }}
            w={160}
          />
        </Group>
        <Button
          variant="subtle"
          onClick={handleExport}
          loading={isExporting}
          disabled={rows.length === 0 || isExporting}>
          Export CSV
        </Button>
      </Group>

      {isError ? (
        <Alert color="red">{parseErrorMessage(error)}</Alert>
      ) : isLoading ? (
        <Spinner loadingText="Loading skill matrix..." />
      ) : rows.length === 0 ? (
        <Text c="dimmed">
          No people match these filters. Try clearing the search, department,
          vendor or status filters.
        </Text>
      ) : (
        <ScrollArea type="auto">
          <Table striped withTableBorder withColumnBorders miw={200 + skills.length * 160}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{position: 'sticky', left: 0, background: 'var(--mantine-color-body)'}}>
                  Person
                </Table.Th>
                {skills.map(skill => (
                  <Table.Th key={skill.skillId} miw={160}>
                    {skill.name}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(row => {
                const cellsBySkill = new Map(
                  row.cells.map(cell => [cell.skillId, cell])
                );
                return (
                  <Table.Tr key={row.userId}>
                    <Table.Td style={{position: 'sticky', left: 0, background: 'var(--mantine-color-body)'}}>
                      {row.userName}
                      {row.employeeId && (
                        <Text size="xs" c="dimmed">
                          {row.employeeId}
                        </Text>
                      )}
                    </Table.Td>
                    {skills.map(skill => {
                      const cell = cellsBySkill.get(skill.skillId);
                      const pill = skillMatrixStatusPill(
                        cell?.status ?? SkillMatrixStatusEnum.Never
                      );
                      const signOffId = cell?.signOffId ?? undefined;
                      return (
                        <Table.Td key={skill.skillId}>
                          <Group gap={4} wrap="nowrap">
                            <Pill text={pill.text} color={pill.color} />
                            {signOffId !== undefined && (
                              <Tooltip label="View sign-off detail" position="top" withArrow>
                                <Anchor
                                  component={Link}
                                  to={PathUtility.TrainingSignOffDetail(
                                      String(signOffId)
                                    )}
                                  aria-label="View sign-off detail"
                                  display="inline-flex"
                                  style={{alignItems: 'center'}}>
                                  <IconExternalLink size={14} />
                                </Anchor>
                              </Tooltip>
                            )}
                          </Group>
                          {cell?.expiresAt && (
                            <Text size="xs" c="dimmed" mt={2}>
                              Expires{' '}
                              {parseUtcDate(cell.expiresAt).toLocaleDateString()}
                            </Text>
                          )}
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}

      {!isError && !isLoading && (data?.totalRecords ?? 0) > 0 && (
        <Paginator
          mt="md"
          currentPage={page}
          totalRecords={data?.totalRecords ?? 0}
          perPage={PAGE_SIZE}
          onChange={handlePageChange}
        />
      )}
      {isFetching && !isLoading && (
        <Text size="xs" c="dimmed" mt="xs">
          Refreshing...
        </Text>
      )}
    </Box>
  );
};
